import { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button, Chip, FontAwesomeIcon } from '@appcharge/shared-ui';
import { CellContext, RowData } from '@tanstack/react-table';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import { Offer } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import {
  EFeatureFlag,
  EOfferStatus,
  OfferType,
  SortingDirection,
  SortingOffersValue
} from 'constants/enums';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { CircleFadingPlus } from 'lucide-react';
import {
  appearanceUtils,
  statusMapping,
  statusUtils
} from 'utils/scheduleUtils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';
import EmptyState from 'components/EmptyState/EmptyState';
import FilterDropdownCard from 'components/Filter/FilterDropdownCard';
import FilterSearchCard from 'components/Filter/FilterSearchCard';
import Input from 'components/Input/Input';
import PortalHeader from 'components/PortalHeader/PortalHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from 'components/ui';

import { EToasterStatus, showToast } from '../../utils/showToast';

const RollingOffersTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canUserEdit } = usePermissions();
  const { isActionEnabled } = useUserPermissions();
  const { page, rows, sortValue, direction } = usePaginationFromURL('offers');

  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage, setRowsPerPage] = useState(rows);
  const [sorting, setSorting] = useState<{
    sortValue: SortingOffersValue;
    direction: SortingDirection;
  }>({
    sortValue: sortValue,
    direction: direction
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [cheatsOfferId, setCheatsOfferId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState('');
  const [subOffer, setSubOffer] = useState(1);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showLimitedEditModal, setShowLimitedEditModal] = useState(false);

  const { getOffers, deleteOffer, toggleOfferStatus, updateCollectedIndex } =
    useOffers({
      offerType: OfferType.ROLLING_OFFER,
      customPagination: { currentPage, rowsPerPage },
      sorting
    });

  const totalCount = getOffers.data?.totalCount || 0;
  const { featureFlags } = useFeatureFlags({ publisherId });

  const hasFeatureFlagCheats =
    featureFlags?.[
    EFeatureFlag.DASHBOARD_ROLLING_CHEATS
    ];

  const statusOptions = useMemo(() =>
    Object.values(EOfferStatus)
      .filter((status) => status !== EOfferStatus.PENDING_SAVE)
      .map((status) => ({ label: status, value: status })),
    []);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rowsPerPage));
    queryParams.set('sortValue', sorting.sortValue);
    queryParams.set('direction', sorting.direction);

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getOffers.refetch();
  }, [currentPage, rowsPerPage, sorting]);

  const handleCloseCheats = () => {
    setCheatsOfferId(null);
    setPlayerId('');
    setSubOffer(1);
  };

  const handleEditOfferUI = (offerId: string): void => {
    navigate(`./form/${offerId}`);
  };

  const handleDuplicateOffer = (offerId: string): void => {
    navigate(`./dup/${offerId}`);
  };

  const handleDeleteOffer = () => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Rolling Offer deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete rolling Offer',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      }
    });
  };

  const handleRunCheats = () => {
    updateCollectedIndex.mutate(
      {
        indexCollected: subOffer - 1,
        playerId,
        offerId: cheatsOfferId
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Cheats successfully created',
            status: EToasterStatus.SUCCESS
          });
          handleCloseCheats();
        },
        onError: () => {
          showToast({
            message: 'Error creating cheats',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const toggleRollingStatus = async (offerId: string, active: boolean) => {
    toggleOfferStatus.mutate(
      { active: !active, offerId },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Rolling Offer deactivated successfully'
              : 'Rolling Offer activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing rolling offer',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const offers: Offer[] =
    (!getOffers.isLoading &&
      getOffers?.data?.offers &&
      getOffers?.data?.offers.map((offer: Offer) => ({
        _id: offer.publisherOfferId,
        ...offer
      }))) ||
    [];

  const COLUMNS: any = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'offerId',
      header: 'ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'subOffers',
      header: 'Sub Offers',
      cell: ({ row }: any) => row.original.productsSequence?.length || 0
    },

    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<RowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'nextAppearance',
      header: 'Next Appearance',
      cell: ({ row }: any) => {
        const schedule = (row.original as Offer)?.schedule;
        if (schedule?.permanent) return '-';
        return appearanceUtils.getNextUpcomingTime(schedule?.timeFrames) || '-';
      }
    },
    {
      accessorKey: 'actions',
      header: null,
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }: any) => (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              ...(hasFeatureFlagCheats
                ? [
                  {
                    label: 'Cheats',
                    handler: () => {
                      setCheatsOfferId(row.original.offerId);
                    },
                    disabled:
                      !canUserEdit ||
                      !isActionEnabled(
                        null,
                        EPermissionGroup.OFFERS,
                        EPermissionAction.MANAGE,
                        publisherId
                      )
                  }
                ]
                : []),
              {
                label: 'Edit',
                handler: () => {
                  handleEditOfferUI(row.original.offerId);
                },
                disabled:
                  !canUserEdit ||
                  !isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  ),
                hidden: !isActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
              },
              {
                label: 'Edit',
                handler: () => {
                  setSelectedOffer(row.original.offerId);
                  setShowLimitedEditModal(true);
                },
                disabled:
                  !canUserEdit ||
                  !isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ),
                hidden:
                  !isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ) ||
                  isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: row.original.active ? 'Deactivate' : 'Activate',
                handler: () =>
                  toggleRollingStatus(
                    row.original.offerId,
                    row.original.active
                  ),
                disabled:
                  !canUserEdit ||
                  !isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS,
                    publisherId
                  )
              },
              {
                label: 'Duplicate',
                handler: () => handleDuplicateOffer(row.original.offerId),
                disabled:
                  !canUserEdit ||
                  !isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: 'Delete',
                handler: () => {
                  setIsDeleteDialogOpen(true);
                  setSelectedOffer(row.original.offerId);
                },
                disabled:
                  !canUserEdit ||
                  !isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              }
            ]}
          />
        </div>
      )
    }
  ];

  const filteredOffers = offers.filter((offer: Offer) => {
    const lowerExternalId = offer.publisherOfferId.toLowerCase();
    const lowerName = offer.name.toLowerCase();
    const offerStatus = statusUtils.getOfferStatus(offer, offer.active);
    const matchesStatus = !statusFilter || statusFilter === offerStatus;

    return (
      lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
      lowerName.includes(nameFilter.toLowerCase()) &&
      matchesStatus
    );
  });

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const BUTTON_TEXT = `${offers.length > 0 ? 'Add' : 'Create'} New Rolling Offer`;

  const renderContent = () => {
    if (offers.length === 0 && !getOffers.isLoading && !getOffers.isFetching) {
      return (
        <EmptyState
          title="Add your first Rolling Offer"
          description={<>Rolling Offers unlock new sub-offers as players progress. Purchasing or<br />collecting an offer reveals the next, motivating ongoing engagement before<br />the promotion resets.</>}
          buttonText={BUTTON_TEXT}
          icon={<CircleFadingPlus className="text-base-mutedForeground h-6 w-6" />}
          disableButton={!isActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )}
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredOffers}
          columns={COLUMNS}
          rowIdIdentifier="offerId"
          bodyDataTestId="dashRollingOfferList"
          rowCount={totalCount}
          rowsPerPage={rowsPerPage}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
          isLoading={getOffers.isLoading || getOffers.isFetching}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title="Rolling Offers"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !isActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Rolling Offers External ID"
            filterValue={externalIdFilter}
            setFilterValue={setExternalIdFilter}
            isLabelSearchBy
          />
          <FilterSearchCard
            header="Rolling Offers Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
          />
          <FilterDropdownCard
            header="Status"
            filterValue={statusFilter}
            setFilterValue={setStatusFilter}
            options={statusOptions}
            dropdownMinWidth={200}
          />
        </div>
      )}
      {renderContent()}
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedOffer || ''}
          offerDesignType={OfferDesignTypes.RollingOffer}
          onClose={() => setShowLimitedEditModal(false)}
          isOpen={showLimitedEditModal}
          offerType={OfferType.ROLLING_OFFER}
        />
      )}
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          onOpenChange={(open) => {
            if (!open) setIsDeleteDialogOpen(false);
          }}
          title={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_ROLLING_OFFER_POPUP].title
          }
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_ROLLING_OFFER_POPUP]
              .description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOffer },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
      <Dialog open={!!cheatsOfferId} onOpenChange={(open) => !open && handleCloseCheats()}>
        <DialogContent className="bg-white p-0 max-w-[600px] rounded-lg">
          <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b border-base-border">
            <DialogTitle className="text-base-mutedForeground text-p-l font-normal">
              Cheats
            </DialogTitle>
            <div className="flex items-center">
              <div className="w-px h-[24px] bg-base-border mr-[16px]" />
              <FontAwesomeIcon
                icon="fa-regular fa-xmark"
                className="w-[12px] h-[14px] cursor-pointer text-base-foreground hover:text-base-mutedForeground transition-colors"
                onClick={handleCloseCheats}
              />
            </div>
          </DialogHeader>
          <div className="px-[84px] pt-[25px] pb-[40px]">
            <h2 className="text-[20px] font-semibold text-base-foreground mb-[6px]">
              Move Player to Sub Offer
            </h2>
            <p className="text-[16px] font-normal text-base-mutedForeground mb-[20px]">
              Enter Player ID and select the destination offer
            </p>

            <Input
              label="Player ID"
              placeholder="ID number"
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              className="mb-[20px]"
            />

            <div className="flex items-end gap-[8px]">
              <Input
                label="Move to"
                placeholder="Enter sub offer number"
                value={subOffer}
                onChange={(e) => setSubOffer(Number(e.target.value))}
                type="number"
                className="flex-1"
              />
              <Button
                onClick={handleRunCheats}
                disabled={!playerId}
                className="w-[77px] h-[40px] bg-base-foreground hover:bg-base-foreground/90 text-white"
              >
                Run
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RollingOffersTable;
